import { useState, useRef, useEffect } from 'react';
import { Note, Tag, SimilarNote, getSimilarNotes } from '../../services/notes';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Ellipsis, ArrowDownFromLine, ArrowUpToLine, Search, Edit2, Trash2, ChevronsDown, ChevronsUp } from 'lucide-react';
//import NoteEditor from '../NoteEditor';
import TTEditor from '../TTEditor';
import './styles.css';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface SimpleNoteListProps {
  notes: Note[];
  loading: boolean;
  onDelete: (noteId: string) => Promise<boolean>;
  onEdit: (note: Note) => void;
  onInlineEdit: (noteId: string, content: string, updates?: Partial<Note>) => Promise<boolean>;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  total: number;
}

interface NoteProps {
  note: Note;
  onDelete: (id: string) => Promise<boolean>;
  onEdit: (note: Note) => void;
  onInlineEdit: (id: string, content: string, updates?: Partial<Note>) => Promise<boolean>;
}

const SimpleNoteItem: React.FC<NoteProps> = ({ note, onDelete, onEdit, onInlineEdit }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showSimilar, setShowSimilar] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpand, setNeedsExpand] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [similarNotes, setSimilarNotes] = useState<SimilarNote[]>([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const { t } = useTranslation('notelist');
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showSimilar && !similarNotes.length) {
      loadSimilarNotes();
    }
  }, [showSimilar]);

  useEffect(() => {
    if (contentRef.current) {
      const { scrollHeight, clientHeight } = contentRef.current;
      setNeedsExpand(scrollHeight > 300);
    }
  }, [note.text]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const loadSimilarNotes = async () => {
    setLoadingSimilar(true);
    try {
      const similar = await getSimilarNotes(note._id);
      setSimilarNotes(similar);
    } catch (error) {
      console.error('Failed to load similar notes:', error);
    } finally {
      setLoadingSimilar(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('notelist.delete_confirm'))) {
      return;
    }
    const success = await onDelete(note._id);
    if (!success) {
      alert(t('notelist.delete_fail'));
    }
  };

  const getSource = () => {
    if (note.url) {
      try {
        const url = new URL(note.url);
        return url.hostname;
      } catch {
        return note.url;
      }
    }
    return '';
  };

  const formatDate = (date: string) => {
    return format(new Date(date), 'MMM d');
  };

  if (isEditing) {
    return (
      <div className="inline-editor">
        {/* <NoteEditor */}
        <TTEditor
          initialNote={note}
          onSave={async (content, updates) => {
            const success = await onInlineEdit(note._id, content, updates);
            if (success) setIsEditing(false);
            return success;
          }}
          onCancel={() => setIsEditing(false)}
          inline={true}
        />
      </div>
    );
  }

  return (
    <div className="simple-note-item">
      <div className="simple-note-header">
        <div className="simple-note-meta">
          <span>{formatDate(note.updatedAt || note.createdAt)}</span>
          <span>•</span>
          <span>{getSource()}</span>
          {note.tags && note.tags.length > 0 && (
            <>
              <span>•</span>
              <div className="note-tags">
                {note.tags.map((tag, index) => (
                  <span key={index} className="tag-badge">
                    {tag.level1}
                    {tag.level2 && ` / ${tag.level2}`}
                    {tag.level3 && ` / ${tag.level3}`}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="simple-note-menu" ref={menuRef}>
          <button className="menu-button" onClick={() => setShowMenu(!showMenu)}>
            <Ellipsis size={16} />
          </button>
          {showMenu && (
            <div className="menu-dropdown">
              <button onClick={() => {
                setShowSimilar(!showSimilar);
                setShowMenu(false);
              }}>
                <Search size={16} />
                <span>{showSimilar ? t('notelist.collapse_similar') : t('notelist.view_similar')}</span>
              </button>
              <button onClick={() => {
                setIsEditing(true);
                setShowMenu(false);
              }}>
                <Edit2 size={16} />
                <span>{t('notelist.edit')}</span>
              </button>
              <button onClick={() => {
                handleDelete();
                setShowMenu(false);
              }}>
                <Trash2 size={16} />
                <span>{t('notelist.delete')}</span>
              </button>
            </div>
          )}
        </div>
      </div>
      <div 
        ref={contentRef}
        className={`simple-note-content ${isExpanded ? 'expanded' : ''}`}
        style={{ maxHeight: isExpanded ? 'none' : '300px' }}
      >
        <ReactMarkdown 
          remarkPlugins={[remarkGfm]}
          components={{
            img: ({ node, ...props }) => (
              <img
                {...props}
                loading="lazy"
                onLoad={(e) => {
                  e.currentTarget.classList.remove('loading');
                  if (contentRef.current) {
                    const { scrollHeight, clientHeight } = contentRef.current;
                    setNeedsExpand(scrollHeight > 300);
                  }
                }}
                className="loading"
                onClick={() => {
                  // 可以添加图片点击预览功能
                }}
              />
            )
          }}
        >
          {note.text}
        </ReactMarkdown>
      </div>
      {needsExpand && (
        <button 
          className="expand-button"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? (
            <>
              <ChevronsUp size={16} />
              {t('notelist.collapse')}
            </>
          ) : (
            <>
              <ChevronsDown size={16} />
              {t('notelist.expand')}
            </>
          )}
        </button>
      )}
      {note.comment && (
        <div className="note-comment">
          <div className="comment-label">{t('notelist.comments')}</div>
          <div className="comment-text">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              className="markdown-body"
            >
              {note.comment}
            </ReactMarkdown>
          </div>
        </div>
      )}
      {showSimilar && (
        <div className="similar-notes">
          {loadingSimilar ? (
            <div className="loading">{t('notelist.loading_similar')}</div>
          ) : similarNotes.length > 0 ? (
            <div className="similar-notes-list">
              <h4>{t('notelist.similar_notes')}</h4>
              {similarNotes.map(similar => (
                <div key={similar._id} className="similar-note-item">
                  <div className="similar-note-header">
                    <span className="similar-note-title">{similar.title}</span>
                    <span className="similarity-score">
                      {t('notelist.similarity')}: {(similar.similarity * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="similar-note-preview">{similar.text}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-similar">{t('notelist.no_similar')}</div>
          )}
        </div>
      )}
    </div>
  );
};

const SimpleNoteList = ({ 
  notes = [], 
  loading, 
  onDelete, 
  onEdit, 
  onInlineEdit,
  hasMore,
  isLoadingMore,
  onLoadMore,
  total
}: SimpleNoteListProps) => {
  const { t } = useTranslation('notelist');
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (listRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = listRef.current;
        if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !isLoadingMore) {
          onLoadMore();
        }
      }
    };

    const listElement = listRef.current;
    if (listElement) {
      listElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (listElement) {
        listElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [hasMore, isLoadingMore, onLoadMore]);

  if (loading) {
    return <div className="loading">{t('notelist.loading')}</div>;
  }

  if (!notes.length) {
    return <div className="empty-message">{t('notelist.no_notes')}</div>;
  }

  const uniqueNotes = new Map<string, Note>();
  notes.forEach(note => {
    uniqueNotes.set(note._id, note);
  });

  const groupedNotes = Array.from(uniqueNotes.values()).reduce((groups, note) => {
    const groupKey = note.url || 'no-url';
    if (!groups[groupKey]) {
      groups[groupKey] = {
        title: note.title,
        notes: []
      };
    }
    groups[groupKey].notes.push(note);
    return groups;
  }, {} as { [key: string]: { title: string; notes: Note[] } });

  return (
    <div ref={listRef} className="simple-note-list">
      {Object.entries(groupedNotes).map(([url, group]) => (
        <div key={`group-${url}-${group.notes.length}`} className="simple-note-group">
          <div className="simple-group-header">
            <h2 className="simple-group-title">
              {url === 'no-url' ? (
                <span>{group.title}</span>
              ) : url.startsWith('note://') ? (
                <span>{group.title}</span>
              ) : (
                <a href={url} target="_blank" rel="noopener noreferrer">
                  {group.title}
                </a>
              )}
            </h2>
            <span className="simple-note-count">
              {group.notes.length} {group.notes.length > 1 ? t('notelist.notes') : t('notelist.note')}
            </span>
          </div>
          <div className="simple-group-notes">
            {group.notes.map(note => (
              <SimpleNoteItem 
                key={note._id}
                note={note} 
                onDelete={onDelete} 
                onEdit={onEdit}
                onInlineEdit={onInlineEdit}
              />
            ))}
          </div>
        </div>
      ))}
      {isLoadingMore && (
        <div className="loading-more">{t('notelist.loading_more')}</div>
      )}
      {!hasMore && notes.length > 0 && (
        <div className="no-more-notes">
          {t('notelist.no_more_notes', { total })}
        </div>
      )}
    </div>
  );
};

export default SimpleNoteList; 